/**
 * Coupons admin page (/coupons, super_admin only).
 *
 * Tabla con todos los cupones + diálogo para crear/editar.
 * Soporta: tipo (monto fijo MXN o porcentaje), límite total de usos,
 * límite por usuario, compra mínima, vigencia (desde / hasta) y on/off.
 */
import { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Plus, PencilSimple, Trash, Ticket, Percent, CurrencyDollar, Calendar, Users,
  CheckCircle, XCircle,
} from "@phosphor-icons/react";
import { toast } from "sonner";

const EMPTY = {
  code: "", discount_type: "percent", discount_value: 10,
  max_uses: "", max_uses_per_user: "", min_subtotal_mxn: "",
  valid_from: "", valid_until: "", active: true, description: "",
};

const fmtDate = (iso) => iso ? new Date(iso).toLocaleDateString("es-MX") : "—";
const fmtMXN = (n) => `$${Number(n || 0).toLocaleString("es-MX", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function Coupons() {
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/coupons");
      setCoupons(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error cargando cupones");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => { setEditingId(null); setForm(EMPTY); setShowForm(true); };
  const openEdit = (c) => {
    setEditingId(c.id);
    setForm({
      code: c.code,
      discount_type: c.discount_type,
      discount_value: c.discount_value,
      max_uses: c.max_uses ?? "",
      max_uses_per_user: c.max_uses_per_user ?? "",
      min_subtotal_mxn: c.min_subtotal_mxn ?? "",
      valid_from: c.valid_from ? c.valid_from.slice(0, 10) : "",
      valid_until: c.valid_until ? c.valid_until.slice(0, 10) : "",
      active: c.active,
      description: c.description || "",
    });
    setShowForm(true);
  };

  const save = async () => {
    if (!form.code.trim()) return toast.error("El código es obligatorio");
    if (!form.discount_value || Number(form.discount_value) <= 0) return toast.error("Valor de descuento inválido");
    if (form.discount_type === "percent" && Number(form.discount_value) > 100) {
      return toast.error("El porcentaje no puede ser mayor a 100");
    }
    setSaving(true);
    try {
      const payload = {
        code: form.code.trim(),
        discount_type: form.discount_type,
        discount_value: Number(form.discount_value),
        max_uses: form.max_uses === "" ? null : Number(form.max_uses),
        max_uses_per_user: form.max_uses_per_user === "" ? null : Number(form.max_uses_per_user),
        min_subtotal_mxn: form.min_subtotal_mxn === "" ? null : Number(form.min_subtotal_mxn),
        valid_from: form.valid_from || null,
        valid_until: form.valid_until || null,
        active: !!form.active,
        description: form.description.trim(),
      };
      if (editingId) {
        // PATCH doesn't allow editing the code; remove from payload
        const { code: _, ...patch } = payload;
        await api.patch(`/coupons/${editingId}`, patch);
      } else {
        await api.post("/coupons", payload);
      }
      toast.success(editingId ? "Cupón actualizado" : "Cupón creado");
      setShowForm(false);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error guardando cupón");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (c) => {
    if (!window.confirm(`¿Eliminar el cupón ${c.code}?`)) return;
    try {
      await api.delete(`/coupons/${c.id}`);
      toast.success("Cupón eliminado");
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error eliminando");
    }
  };

  const toggleActive = async (c) => {
    try {
      await api.patch(`/coupons/${c.id}`, { active: !c.active });
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error");
    }
  };

  return (
    <div className="p-6 sm:p-8 space-y-6">
      <header className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="font-mono-data text-xs tracking-widest text-zinc-500 mb-1">MARKETING</div>
          <h1 className="font-heading text-4xl font-extrabold tracking-tight inline-flex items-center gap-3">
            <Ticket size={32} weight="duotone" className="text-[#D9A93B]" />
            Cupones
          </h1>
          <p className="text-zinc-500 mt-1 text-sm">{coupons.length} cupones · descuentos para la tienda pública</p>
        </div>
        <Button onClick={openCreate} className="gap-2 bg-[#002FA7] hover:bg-[#002277] text-white" data-testid="create-coupon">
          <Plus size={14} weight="bold" /> Nuevo cupón
        </Button>
      </header>

      <div className="border border-zinc-200 bg-white overflow-x-auto">
        <table className="w-full text-sm" data-testid="coupons-table">
          <thead className="bg-zinc-50 text-xs uppercase text-zinc-500">
            <tr>
              <th className="text-left px-4 py-3">Código</th>
              <th className="text-left px-4 py-3">Descuento</th>
              <th className="text-left px-4 py-3">Compra mín.</th>
              <th className="text-left px-4 py-3">Usos</th>
              <th className="text-left px-4 py-3">Por usuario</th>
              <th className="text-left px-4 py-3">Vigencia</th>
              <th className="text-left px-4 py-3">Estado</th>
              <th className="text-right px-4 py-3">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={8} className="px-4 py-16 text-center text-zinc-400">Cargando…</td></tr>}
            {!loading && coupons.length === 0 && (
              <tr><td colSpan={8} className="px-4 py-16 text-center text-zinc-400 dotted-bg">Sin cupones. Crea el primero.</td></tr>
            )}
            {!loading && coupons.map((c) => (
              <tr key={c.id} className="border-t border-zinc-100 hover:bg-zinc-50" data-testid={`coupon-row-${c.code}`}>
                <td className="px-4 py-3 font-mono-data font-bold text-[#002FA7]">{c.code}</td>
                <td className="px-4 py-3">
                  <span className="inline-flex items-center gap-1 font-semibold">
                    {c.discount_type === "percent" ? (
                      <><Percent size={12} weight="bold" /> {c.discount_value}%</>
                    ) : (
                      <><CurrencyDollar size={12} weight="bold" /> {fmtMXN(c.discount_value)}</>
                    )}
                  </span>
                </td>
                <td className="px-4 py-3 text-zinc-600 numeric">{c.min_subtotal_mxn ? fmtMXN(c.min_subtotal_mxn) : "—"}</td>
                <td className="px-4 py-3 text-zinc-700 numeric">{c.uses_count}{c.max_uses ? ` / ${c.max_uses}` : ""}</td>
                <td className="px-4 py-3 text-zinc-600 numeric">{c.max_uses_per_user || "Ilimitado"}</td>
                <td className="px-4 py-3 text-zinc-600 text-xs">
                  {c.valid_from && <div>desde {fmtDate(c.valid_from)}</div>}
                  {c.valid_until && <div>hasta <span className="font-semibold">{fmtDate(c.valid_until)}</span></div>}
                  {!c.valid_from && !c.valid_until && "—"}
                </td>
                <td className="px-4 py-3">
                  <button
                    onClick={() => toggleActive(c)}
                    className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-semibold ${c.active ? "bg-emerald-50 text-emerald-700 hover:bg-emerald-100" : "bg-zinc-100 text-zinc-500 hover:bg-zinc-200"}`}
                    data-testid={`toggle-active-${c.code}`}
                  >
                    {c.active ? <><CheckCircle size={11} weight="fill" /> Activo</> : <><XCircle size={11} weight="fill" /> Inactivo</>}
                  </button>
                </td>
                <td className="px-4 py-3 text-right">
                  <button onClick={() => openEdit(c)} className="p-1.5 hover:bg-zinc-100 rounded" title="Editar" data-testid={`edit-${c.code}`}>
                    <PencilSimple size={14} weight="bold" />
                  </button>
                  <button onClick={() => remove(c)} className="p-1.5 hover:bg-red-50 text-red-600 rounded ml-1" title="Eliminar" data-testid={`delete-${c.code}`}>
                    <Trash size={14} weight="bold" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Form Dialog */}
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="max-w-2xl" data-testid="coupon-form-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl">{editingId ? "Editar cupón" : "Nuevo cupón"}</DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs uppercase tracking-wider">Código *</Label>
                <Input
                  value={form.code}
                  onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })}
                  placeholder="BIENVENIDA10"
                  className="mt-1 font-mono-data uppercase"
                  disabled={!!editingId}
                  data-testid="coupon-code"
                />
                {editingId && <p className="text-[10px] text-zinc-500 mt-1">El código no se puede modificar.</p>}
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider">Descripción</Label>
                <Input
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  placeholder="Bienvenida 10% off"
                  className="mt-1"
                  data-testid="coupon-description"
                />
              </div>
            </div>

            <div className="grid grid-cols-3 gap-3">
              <div className="col-span-1">
                <Label className="text-xs uppercase tracking-wider">Tipo</Label>
                <div className="mt-1 flex gap-1">
                  <button
                    type="button"
                    onClick={() => setForm({ ...form, discount_type: "percent" })}
                    className={`flex-1 px-3 py-2 text-xs font-semibold rounded border ${form.discount_type === "percent" ? "bg-[#002FA7] text-white border-transparent" : "bg-white border-zinc-200"}`}
                    data-testid="type-percent"
                  >
                    <Percent size={12} weight="bold" className="inline mr-1" /> %
                  </button>
                  <button
                    type="button"
                    onClick={() => setForm({ ...form, discount_type: "amount" })}
                    className={`flex-1 px-3 py-2 text-xs font-semibold rounded border ${form.discount_type === "amount" ? "bg-[#002FA7] text-white border-transparent" : "bg-white border-zinc-200"}`}
                    data-testid="type-amount"
                  >
                    <CurrencyDollar size={12} weight="bold" className="inline mr-1" /> MXN
                  </button>
                </div>
              </div>
              <div className="col-span-1">
                <Label className="text-xs uppercase tracking-wider">Valor *</Label>
                <Input
                  type="number"
                  min="0"
                  step={form.discount_type === "percent" ? "1" : "10"}
                  max={form.discount_type === "percent" ? "100" : undefined}
                  value={form.discount_value}
                  onChange={(e) => setForm({ ...form, discount_value: e.target.value })}
                  className="mt-1 numeric"
                  data-testid="coupon-value"
                />
              </div>
              <div className="col-span-1">
                <Label className="text-xs uppercase tracking-wider">Compra mín. (MXN)</Label>
                <Input
                  type="number"
                  min="0"
                  step="50"
                  value={form.min_subtotal_mxn}
                  onChange={(e) => setForm({ ...form, min_subtotal_mxn: e.target.value })}
                  placeholder="Opcional"
                  className="mt-1 numeric"
                  data-testid="coupon-min-subtotal"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs uppercase tracking-wider inline-flex items-center gap-1"><Users size={12} weight="bold" /> Máx. de usos totales</Label>
                <Input
                  type="number"
                  min="1"
                  value={form.max_uses}
                  onChange={(e) => setForm({ ...form, max_uses: e.target.value })}
                  placeholder="Ilimitado"
                  className="mt-1 numeric"
                  data-testid="coupon-max-uses"
                />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider">Máx. por usuario</Label>
                <Input
                  type="number"
                  min="1"
                  value={form.max_uses_per_user}
                  onChange={(e) => setForm({ ...form, max_uses_per_user: e.target.value })}
                  placeholder="Ilimitado"
                  className="mt-1 numeric"
                  data-testid="coupon-max-per-user"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs uppercase tracking-wider inline-flex items-center gap-1"><Calendar size={12} weight="bold" /> Válido desde</Label>
                <Input
                  type="date"
                  value={form.valid_from}
                  onChange={(e) => setForm({ ...form, valid_from: e.target.value })}
                  className="mt-1"
                  data-testid="coupon-valid-from"
                />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider">Válido hasta</Label>
                <Input
                  type="date"
                  value={form.valid_until}
                  onChange={(e) => setForm({ ...form, valid_until: e.target.value })}
                  className="mt-1"
                  data-testid="coupon-valid-until"
                />
              </div>
            </div>

            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={form.active}
                onChange={(e) => setForm({ ...form, active: e.target.checked })}
                className="w-4 h-4"
                data-testid="coupon-active"
              />
              <span className="text-sm">Cupón activo</span>
            </label>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowForm(false)}>Cancelar</Button>
            <Button onClick={save} disabled={saving} className="bg-[#002FA7] hover:bg-[#002277] text-white" data-testid="save-coupon">
              {saving ? "Guardando…" : editingId ? "Guardar cambios" : "Crear cupón"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
